import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { makeAutoObservable, reaction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { DomainProductLineItem, DomainProductLinesDelegate } from '../domainProductLinesDelegate';

interface DomainParams {
  rateSegment: RateSegment;
  disableBCFields?: boolean;
}

type DefaultProposalBaseFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
};

export class DefaultProposalFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public proposalForm: FormController<DefaultProposalBaseFields>;

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this.domainProductLinesDelegate.domainProductLineItems;
  }

  public get isAvailableProductLinesLoading(): boolean {
    return this.domainProductLinesDelegate.isLoading;
  }

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  private get _canFetchProductLines(): boolean {
    const {
      fields: { effectiveDate, zip, state, county },
    } = this.proposalForm;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg
    );
  }

  constructor(params: DomainParams) {
    const disableBCFields = params.disableBCFields || false;

    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>('', { disabled: disableBCFields }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false, disabled: disableBCFields },
              state: {
                disabled: disableBCFields,
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
              county: { disabled: disableBCFields },
            },
          },
        ],
      },
    );

    this.domainProductLinesDelegate = new DomainProductLinesDelegate({
      rateSegment: params.rateSegment,
    });

    reaction(
      () => [
        this.proposalForm.fields.effectiveDate.value,
        this.proposalForm.fields.zip.value,
        this.proposalForm.fields.state.value,
        this.proposalForm.fields.county.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.proposalForm.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
